// Service de gestion d'erreurs centralisé amélioré
class ErrorHandler {
  static ERROR_TYPES = {
    NETWORK: 'network',
    VALIDATION: 'validation',
    AUTHENTICATION: 'authentication',
    PERMISSION: 'permission',
    API: 'api',
    UI: 'ui',
    UNKNOWN: 'unknown',
  };

  static STORAGE_KEY = 'app_errors';
  static MAX_ERRORS = 50;
  static MAX_ERROR_SIZE = 5000; // Limite de caractères pour éviter les erreurs trop volumineuses

  static logError(
    error,
    context = 'Unknown',
    errorType = this.ERROR_TYPES.UNKNOWN,
    additionalData = {}
  ) {
    try {
      // Normaliser l'erreur
      const normalizedError = this.normalizeError(error);

      // Log en console pour développement
      console.group(`🚨 [${errorType.toUpperCase()}] ${context}`);
      console.error('Message:', normalizedError.message);
      if (normalizedError.stack) {
        console.error('Stack:', normalizedError.stack);
      }
      if (Object.keys(additionalData).length > 0) {
        console.error('Additional Data:', additionalData);
      }
      console.groupEnd();

      // Préparer les données d'erreur
      const errorData = {
        id: this.generateErrorId(),
        message: normalizedError.message,
        stack: normalizedError.stack,
        type: errorType,
        timestamp: new Date().toISOString(),
        context,
        userAgent: this.getUserAgent(),
        url: this.getCurrentUrl(),
        additionalData: this.sanitizeAdditionalData(additionalData),
        severity: this.determineSeverity(errorType),
      };

      // Sauvegarder dans localStorage si disponible
      this.saveErrorToStorage(errorData);

      // En production, envoyer au service de monitoring
      if (process.env.NODE_ENV === 'production') {
        this.sendToMonitoring(errorData);
      }

      return errorData.id;
    } catch (handlerError) {
      // Si le gestionnaire d'erreur lui-même échoue, log minimal
      console.error('ErrorHandler failed:', handlerError);
      console.error('Original error:', error);
    }
  }

  static normalizeError(error) {
    if (!error) {
      return { message: 'Unknown error occurred', stack: null };
    }

    if (typeof error === 'string') {
      return { message: error, stack: null };
    }

    if (error instanceof Error) {
      return {
        message: error.message || 'Error without message',
        stack: error.stack || null,
        name: error.name || 'Error',
      };
    }

    // Si c'est un objet avec des propriétés d'erreur
    if (typeof error === 'object') {
      return {
        message: error.message || error.error || error.msg || JSON.stringify(error),
        stack: error.stack || null,
        name: error.name || 'Unknown',
      };
    }

    return { message: String(error), stack: null };
  }

  static generateErrorId() {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  static getUserAgent() {
    try {
      return navigator?.userAgent || 'Unknown';
    } catch {
      return 'Unknown';
    }
  }

  static getCurrentUrl() {
    try {
      return window?.location?.href || 'Unknown';
    } catch {
      return 'Unknown';
    }
  }

  static sanitizeAdditionalData(data) {
    try {
      const sanitized = JSON.parse(JSON.stringify(data));
      const stringified = JSON.stringify(sanitized);

      // Limiter la taille des données additionnelles
      if (stringified.length > this.MAX_ERROR_SIZE) {
        return {
          ...sanitized,
          _truncated: true,
          _originalSize: stringified.length,
        };
      }

      return sanitized;
    } catch {
      return { _error: 'Could not serialize additional data' };
    }
  }

  static determineSeverity(errorType) {
    const severityMap = {
      [this.ERROR_TYPES.NETWORK]: 'medium',
      [this.ERROR_TYPES.VALIDATION]: 'low',
      [this.ERROR_TYPES.AUTHENTICATION]: 'high',
      [this.ERROR_TYPES.PERMISSION]: 'high',
      [this.ERROR_TYPES.API]: 'medium',
      [this.ERROR_TYPES.UI]: 'low',
      [this.ERROR_TYPES.UNKNOWN]: 'medium',
    };

    return severityMap[errorType] || 'medium';
  }

  static saveErrorToStorage(errorData) {
    try {
      if (typeof Storage === 'undefined') {
        console.warn('localStorage not available');
        return;
      }

      const errors = this.getStoredErrors();
      errors.push(errorData);

      // Garder seulement les dernières erreurs
      const recentErrors = errors.slice(-this.MAX_ERRORS);

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(recentErrors));
    } catch (storageError) {
      console.warn('Failed to save error to localStorage:', storageError);
    }
  }

  static async sendToMonitoring(errorData) {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');

      await fetch(`${API_BASE_URL}/api/errors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          error: errorData,
          timestamp: new Date().toISOString(),
          source: 'frontend',
        }),
      });
    } catch (monitoringError) {
      console.warn('Failed to send error to monitoring:', monitoringError);
      // Ne pas créer une boucle d'erreurs en appelant logError ici
    }
  }

  static getStoredErrors() {
    try {
      if (typeof Storage === 'undefined') {
        return [];
      }

      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  static clearStoredErrors() {
    try {
      if (typeof Storage !== 'undefined') {
        localStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      console.warn('Failed to clear stored errors:', error);
    }
  }

  static getErrorStats() {
    const errors = this.getStoredErrors();
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const oneDayAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    return {
      total: errors.length,
      lastHour: errors.filter((e) => new Date(e.timestamp) > oneHourAgo).length,
      last24Hours: errors.filter((e) => new Date(e.timestamp) > oneDayAgo).length,
      byType: errors.reduce((acc, error) => {
        acc[error.type] = (acc[error.type] || 0) + 1;
        return acc;
      }, {}),
      bySeverity: errors.reduce((acc, error) => {
        acc[error.severity] = (acc[error.severity] || 0) + 1;
        return acc;
      }, {}),
    };
  }

  // Méthodes utilitaires pour différents types d'erreurs
  static logNetworkError(error, context, additionalData = {}) {
    return this.logError(error, context, this.ERROR_TYPES.NETWORK, additionalData);
  }

  static logValidationError(error, context, additionalData = {}) {
    return this.logError(error, context, this.ERROR_TYPES.VALIDATION, additionalData);
  }

  static logAuthError(error, context, additionalData = {}) {
    return this.logError(error, context, this.ERROR_TYPES.AUTHENTICATION, additionalData);
  }

  static logApiError(error, context, additionalData = {}) {
    return this.logError(error, context, this.ERROR_TYPES.API, additionalData);
  }

  static logUIError(error, context, additionalData = {}) {
    return this.logError(error, context, this.ERROR_TYPES.UI, additionalData);
  }
}

export default ErrorHandler;
